/**
 * Image utility functions for handling profile image uploads
 */

/**
 * Compress and convert image to base64
 * @param {File} file - Image file
 * @param {number} maxWidth - Maximum width (default: 300)
 * @param {number} maxHeight - Maximum height (default: 300)
 * @param {number} quality - JPEG quality (0-1, default: 0.8)
 * @returns {Promise<string>} Base64 data URL
 */
export const compressAndConvertToBase64 = (file, maxWidth = 300, maxHeight = 300, quality = 0.8) => {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      // Calculate new dimensions while maintaining aspect ratio
      let { width, height } = img;
      
      if (width > height) {
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
      } else {
        if (height > maxHeight) {
          width = (width * maxHeight) / height;
          height = maxHeight;
        }
      }

      // Set canvas dimensions
      canvas.width = width;
      canvas.height = height;

      // Draw and compress image
      ctx.drawImage(img, 0, 0, width, height);
      
      // Convert to base64 with specified quality
      const base64 = canvas.toDataURL('image/jpeg', quality);
      resolve(base64);
    };

    img.onerror = () => {
      reject(new Error('Failed to load image'));
    };

    // Create object URL for the file
    const objectUrl = URL.createObjectURL(file);
    img.src = objectUrl;
  });
};

/**
 * Validate image file
 * @param {File} file - Image file
 * @param {number} maxSize - Maximum file size in MB (default: 5)
 * @returns {Object} Validation result
 */
export const validateImageFile = (file, maxSize = 5) => {
  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
  const maxSizeBytes = maxSize * 1024 * 1024; // Convert MB to bytes

  if (!allowedTypes.includes(file.type)) {
    return {
      isValid: false,
      error: 'Please select a valid image file (JPEG, PNG, GIF, or WebP)'
    };
  }

  if (file.size > maxSizeBytes) {
    return {
      isValid: false,
      error: `File size must be less than ${maxSize}MB`
    };
  }

  return { isValid: true };
};

/**
 * Process image file for upload
 * @param {File} file - Image file
 * @returns {Promise<string>} Base64 data URL
 */
export const processImageForUpload = async (file) => {
  // Validate file
  const validation = validateImageFile(file);
  if (!validation.isValid) {
    throw new Error(validation.error);
  }

  // Compress and convert to base64
  try {
    const base64 = await compressAndConvertToBase64(file);
    return base64;
  } catch (error) {
    throw new Error('Failed to process image: ' + error.message);
  }
};

/**
 * Remove image (clear profile image)
 * @returns {string} Default placeholder image
 */
export const removeImage = () => {
  return '/assets/img/dashboard/edit/1.png';
};

export default {
  compressAndConvertToBase64,
  validateImageFile,
  processImageForUpload,
  removeImage
}; 